<template>
  <div id="order-management">
    <div class="header">
      <h1>{{ (myOrder) ? ($t('ORDER_MANAGEMENT_MY_INBOX')) : ($t('ORDER_MANAGEMENT'))}}</h1>

      <live-update-controller channel="orders" :checkConnect="true" @signal="() => fetchOrderList(false)" />

    </div>
    <ul class="status-order">
      <li><strong><span>{{ $t('UNCLAIMED') }}</span><span>: </span><span class="highlight">{{ countUnclaimed }}</span></strong></li>
      <li><strong><span>{{ $t('PENDING') }}</span><span>: </span><span class="highlight">{{ countPending }}</span></strong></li>
      <li><strong><span>{{ $t('CONTACT') }}</span><span>: </span><span class="highlight">{{ countContact }}</span></strong></li>
      <li><strong><span>{{ $t('QUOTED') }}</span><span>: </span><span class="highlight">{{ countQuoted }}</span></strong></li>
      <li><strong><span>{{ $t('APPOINTMENT') }}</span><span>: </span><span class="highlight">{{ countAppointment }}</span></strong></li>
    </ul>

    <div class="search" v-if="isDesktop">
      <text-input
        :placeholder="$t('SEARCH_BY_PHONE_NUMBER')"
        v-model="searchInput"
        @keyup.enter="search"
        rounded-edge
        with-shadow
        borderless
      >
        <template v-slot:icon>
          <font-awesome-icon class="icon" :icon="faSearch" />
        </template>
      </text-input>

      <primary-button class="red-btn" red rounded-corner :text="$t('NEW_ORDER')" @click="() => showCreateOrder()">
        <template v-slot:icon>
          <font-awesome-icon class="icon" :icon="faPlus" />
        </template>
      </primary-button>

    </div>

    <div class="search" v-else>
      <text-input
        :placeholder="$t('SEARCH_BY_PHONE_NUMBER')"
        v-model="searchInput"
        @keyup.enter="search"
        with-shadow
        borderless
      >
        <template v-slot:icon>
          <font-awesome-icon class="icon" :icon="faSearch" />
        </template>
      </text-input>
      <primary-button class="red-btn" :text="$t('NEW_ORDER')" red @click="() => showCreateOrder()" />

    </div>

    <div class="list">
      <table>
        <thead>
          <tr>
            <th colspan="1">
              <div class="d-flex justify-content-center">
                {{ $t('ORDER_ID') }}
                <div class="arrow" :class="{down: filters.sort.includes('-id')}" @click="sorted('id')"><font-awesome-icon :icon="faSortUp" mask="circle"/></div>
                <div class="number-circle"><span>{{ getIndexSort('id') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('LAST_MODIFY') }}
                <div class="arrow" :class="{down: filters.sort.includes('-last_modify')}"><font-awesome-icon @click="sorted('last_modify')" :icon="faSortUp" /></div>
                <div class="number-circle"><span>{{ getIndexSort('last_modify') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('PERSON_IN_CHARGE') }}
                <div class="arrow" :class="{down: filters.sort.includes('-order_staff')}"><font-awesome-icon @click="sorted('order_staff')" :icon="faSortUp" /></div>
                <div class="number-circle"><span>{{ getIndexSort('order_staff') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('STAGE') }}
                <div class="arrow" :class="{down: filters.sort.includes('-step_index')}"><font-awesome-icon @click="sorted('step_index')" :icon="faSortUp" /></div>
                <div class="number-circle"><span>{{ getIndexSort('step_index') }}</span></div>
              </div>
            </th>
            <th>
              <div class="d-flex justify-content-center">
                {{ $t('NAME_CLI') }}
                <div class="arrow" :class="{down: filters.sort.includes('-order_name')}"><font-awesome-icon @click="sorted('order_name')" :icon="faSortUp" /></div>
                <div class="number-circle"><span>{{ getIndexSort('order_name') }}</span></div>
              </div>
            </th>
            <th>{{ $t('PHONE_CLI') }}</th>
            <th>{{ $t('ASSET_TYPE') }}</th>
            <th>{{ $t('STEP') }}</th>
            <th>{{ $t('ACTION') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in orderList">
            <tr
              :key="`info-${item.id}`"
              @dblclick="() => isDesktop ? showOrderDetail(item): null"
              @click="() => isDesktop ? null : showOrderDetail(item)"
              :class="{
                'cant-work-on': !canWorkOn(item)
              }"
            >
              <td
                :class="{
                  'has-working-agent': item.workingAgent
                }"
              >
                <div class="mobile-header" v-if="!isDesktop">{{ $t('ORDER_ID') }}:</div>
                <div>{{ item.id }}</div>
                <div
                  v-if="item.workingAgent"
                  class="working-agent"
                  :class="{
                    'can-remove': item.workingAgent && canWorkOn(item)
                  }"
                  :title="(item.workingAgent && canWorkOn(item)) ? $t('REMOVE')  : ''"
                  @click="() => (item.workingAgent && canWorkOn(item)) ? removeWorkingAgent(item.id) : null"
                >
                  <small>{{ $t('WORKING_AGENT') }}</small>
                  <br />
                  <small><strong>{{  item.workingAgent ? `${item.workingAgent.firstName} ${item.workingAgent.lastName}` : '' }}</strong></small>
                </div>
              </td>
              <td>
                <div class="mobile-header" v-if="!isDesktop">{{ $t('LAST_MODIFY') }}:</div>
                <div>{{  moment(item.lastModify).format('DD-MM-YYYY HH:mm') }}</div>
              </td>
              <td>
                <div class="mobile-header" v-if="!isDesktop">{{ $t('PERSON_IN_CHARGE') }}:</div>
                <div>{{  item.agent ? `${item.agent.firstName} ${item.agent.lastName}` : '' }}</div>
              </td>
              <td :class="{
                unclaimed: item.step === ORDER_STEP.UNCLAIMED,
                contact: item.step === ORDER_STEP.CONTACT,
                pending: item.step === ORDER_STEP.PENDING,
                appointment: item.step === ORDER_STEP.APPOINTMENT,
                quoted: item.step === ORDER_STEP.QUOTED,
                contract: item.step === ORDER_STEP.CONTRACT
              }"
              >
                <div class="mobile-header" v-if="!isDesktop">{{ $t('STAGE') }}:</div>
                <div>{{  $t(getKeyStepTranslations(item.step)) }}</div>
              </td>
              <td>
                <div class="mobile-header" v-if="!isDesktop">{{ $t('NAME_CLI') }}:</div>
                <div>{{  item.firstName }}</div>
              </td>
              <td class="phone">
                <div class="mobile-header" v-if="!isDesktop">{{ $t('PHONE_CLI') }}:</div>
                <div>{{  item.phone }} <font-awesome-icon :icon="faCopy" class="copy-icon" title="copy" @click="(event) => copyToClipboard(item.phone, event)"/></div>
              </td>
              <td>
                <div class="mobile-header" v-if="!isDesktop">{{ $t('ASSET_TYPE') }}:</div>
                <div>{{  item.assetType }}</div>
              </td>
              <td>
                <div class="mobile-header" v-if="!isDesktop">{{ $t('STEP') }}:</div>
                <div>{{ $t(getKeyStageTranslations(item.stage)) }}</div>
              </td>
              <td class="actions" @click="stopEventShowOrderDetail">
                <div  class="mobile-header" v-if="!isDesktop">{{ $t('ACTION') }}:</div>
                <action-box
                  :step="item.step"
                  :stage="item.stage"
                  :orderId="item.id"
                  :status="item.status"
                  :onHandleUpdateOrderStage="handleUpdateOrderStage"
                  :getListStages="listStages(item.stage, item.step)"
                  :getclaimOrder="claimOrder"
                  :getKeyStageTranslations="getKeyStageTranslations"
                  has-border
                  v-if="canWorkOn(item)"
                />
                <div
                  class="icon call-btn-wrapper"
                  v-if="(item.step !== ORDER_STEP.UNCLAIMED) && canWorkOn(item)"
                  @click="() => startCall(item.phone, item.id, item.stage, item.status, item.step)"
                >
                  <font-awesome-icon :icon="faPhone" />
                </div>
                <div class="icon toggle-notes" v-if="item.step !== ORDER_STEP.UNCLAIMED"
                     @click="() => isDesktop ? toggleNotes(index): showOrderNoteModal(item.id)"
                     :class="{ active: !!item.active }">

                  <font-awesome-icon :icon="faStickyNote" />
                </div>
              </td>
            </tr>

            <order-notes :key="`notes-${item.id}`" :id="item.id" :active="!!item.active" :depr-notes="item.note" />
          </template>
        </tbody>
      </table>
    </div>

    <pagination
      :page="filters.page"
      :pageSize="filters.pageSize"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />

    <edit-order
      :editOrder="editOrder"
      :orderDetail="orderDetail"
      :getStages="getStages"
      :getKeyStageTranslations="getKeyStageTranslations"
      :getKeyStepTranslations="getKeyStepTranslations"
    />

    <order-note-modal :id="idOrder"></order-note-modal>

  </div>
</template>

<script>
import { ref, watch, onMounted } from '@vue/composition-api';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch, faPlus, faPhone, faStickyNote, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { ORDER_STEP, ORDER_STAGE, ORDER_STATUS, PATHS, CALL_REFS } from '@/consts';
import { applyParams } from '@/helpers';

import TextInput from '@/components/TextInput';
import PrimaryButton from '@/components/PrimaryButton';
import Pagination from '@/components/Pagination';
import LiveUpdateController from '@/components/LiveUpdateController';
import logService from '@/services/log';
import logKeys from '@/consts/logKeys';

import ActionBox from './ActionBox';
import OrderNotes from './OrderNotes';
import EditOrder from './EditOrder';
import OrderNoteModal from './OrderNoteModal';

import OrderManagementService from './OrderManagementService';

const { useState, useActions } = createNamespacedHelpers('orderList');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useActions: usePhoneActions } = createNamespacedHelpers('phone');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useActions: useAppActions, useState: useAppState } = createNamespacedHelpers('app');
const { useState: useUserState } = createNamespacedHelpers('user');

export default {
  name: 'OrderManagement',
  components: {
    TextInput,
    FontAwesomeIcon,
    PrimaryButton,
    Pagination,
    ActionBox,
    OrderNotes,
    LiveUpdateController,
    EditOrder,
    OrderNoteModal
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('ORDER_MANAGEMENT');

    return {
      title,
      meta: []
    };
  },
  props: {
    myOrder: Boolean
  },

  setup(props, { root }) {
    const {
      items,
      total,
      filters,
      countUnclaimed,
      countPending,
      countContact,
      countQuoted,
      countAppointment
    } = useState(['items', 'total', 'filters', 'countUnclaimed', 'countPending', 'countContact', 'countQuoted', 'countAppointment']);
    const { info, error } = useFlashActions(['info', 'error']);
    const { applyFilter, getOrderList, getMyOrderList } = useActions(['applyFilter', 'getOrderList', 'getMyOrderList']);
    const { startOutboundCall, setRef } = usePhoneActions(['startOutboundCall', 'setRef']);
    const { toggleEditOrderModal: toggleEditOrder, toggleOrderNoteModal } = useModalActions(['toggleEditOrderModal', 'toggleOrderNoteModal']);
    const { profile } = useUserState(['profile']);
    const { getValidators } = useAppActions(['getValidators']);

    const { validators, isDesktop } = useAppState(['validators', 'isDesktop']);

    const openingNote = ref(null);

    // props to newOrder
    const editOrder = ref('');
    const orderDetail = ref(null);

    // props to orderNoteModal
    const idOrder = ref(0);

    const searchInput = ref('');

    const orderList = ref([]);

    const getStages = (orderStep) => {
      switch(orderStep) {
        case ORDER_STEP.UNCLAIMED:
          return [
            ORDER_STAGE.NOT_ELIGIBLE.val,
            ORDER_STAGE.SPAM.val,
            ORDER_STAGE.UNCLAIMED.val
          ];
        case ORDER_STEP.CONTACT:
          return [
            ORDER_STAGE.WAITING_FOR_MORE_INFO.val,
            ORDER_STAGE.FRAUD.val,
            ORDER_STAGE.CALL_AGAIN_LATER.val,
            ORDER_STAGE.MONEY_FROM_DIFFERENT_CHANNEL.val,
            ORDER_STAGE.WINDOW_SHOPPING.val,
            ORDER_STAGE.UNABLE_TO_APPRAISE_LIQUIDATED.val,
            ORDER_STAGE.NOT_ELIGIBLE.val,
            ORDER_STAGE.NO_PICK_UP.val,
            ORDER_STAGE.TERMS_DISAGREEMENT.val,
            ORDER_STAGE.NOT_IN_SERVICE_REGION.val,
          ];
        case ORDER_STEP.QUOTED:
          return [
            ORDER_STAGE.WAITING_FOR_RATE.val,
            ORDER_STAGE.WAITING_FOR_MORE_INFO.val,
            ORDER_STAGE.FRAUD.val,
            ORDER_STAGE.CALL_AGAIN_LATER.val,
            ORDER_STAGE.MONEY_FROM_DIFFERENT_CHANNEL.val,
            ORDER_STAGE.UNABLE_TO_APPRAISE_LIQUIDATED.val,
            ORDER_STAGE.NO_PICK_UP.val,
            ORDER_STAGE.RATE_DISAGREEMENT.val,
            ORDER_STAGE.TERMS_DISAGREEMENT.val,
            ORDER_STAGE.NOT_IN_SERVICE_REGION.val,
          ];
        case ORDER_STEP.PENDING:
          return [
            ORDER_STAGE.SPAM.val,
            ORDER_STAGE.DUPLICATE.val,
            ORDER_STAGE.ORDER_CLAIMED.val,
          ];
        case ORDER_STEP.APPOINTMENT:
          return [
            ORDER_STAGE.CALL_AGAIN_LATER.val,
            ORDER_STAGE.MONEY_FROM_DIFFERENT_CHANNEL.val,
            ORDER_STAGE.APPOINTMENT_1.val,
            ORDER_STAGE.APPOINTMENT_2.val,
            ORDER_STAGE.APPOINTMENT_3.val,
            ORDER_STAGE.APPOINTMENT_4.val,
            ORDER_STAGE.NO_SHOW.val,
            ORDER_STAGE.NO_PICK_UP.val,
          ];
        case ORDER_STEP.CONTRACT:
          return [
            ORDER_STAGE.FRAUD.val,
            ORDER_STAGE.ACTIVE_CONTRACT.val,
            ORDER_STAGE.NOT_ELIGIBLE.val,
            ORDER_STAGE.RATE_DISAGREEMENT.val,
            ORDER_STAGE.TERMS_DISAGREEMENT.val
          ];
        case ORDER_STEP.COLD_LEAD:
          return [
            ORDER_STAGE.VERY_ANNOYED.val,
            ORDER_STAGE.ANNOYED.val,
            ORDER_STAGE.NOT_INTERESTED.val,
            ORDER_STAGE.NOT_CALLED_YET.val,
            ORDER_STAGE.WRONG_NUMBER.val,
            ORDER_STAGE.UNABLE_TO_CONTACT.val,
            ORDER_STAGE.NOT_PICK_UP.val,
            ORDER_STAGE.CALL_AGAIN_LATER.val
          ];
        case ORDER_STEP.WARM_LEAD:
          return [
            ORDER_STAGE.INTERESTED.val,
            ORDER_STAGE.VERY_INTERESTED.val,
            ORDER_STAGE.DEMAND_IN_THE_FUTURE.val,
            ORDER_STAGE.CLOSE_SALE.val,
            ORDER_STAGE.CHANGE_NUMBER.val,
            ORDER_STAGE.UNABLE_TO_CONTACT.val,
            ORDER_STAGE.NOT_PICK_UP.val,
            ORDER_STAGE.CALL_AGAIN_LATER.val,
          ];
        default:
          return [
            ORDER_STAGE.NOT_ELIGIBLE.val,
            ORDER_STAGE.SPAM.val,
            ORDER_STAGE.UNCLAIMED.val
          ];
      }
    };

    const search = () => {
      applyFilter({
        phone: searchInput.value,
      });
    };

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const getKeyStageTranslations = (inputStage) => Object.keys(ORDER_STAGE).find((key) => ORDER_STAGE[key].val === inputStage);

    const getKeyStepTranslations = (inputStep) => Object.keys(ORDER_STEP).find((key) => ORDER_STEP[key] === inputStep);

    const listStages = (stage, step) => getStages(step).reduce((stages, stageList) => {
      if (stageList !== stage
          && stageList !== ORDER_STAGE.CALL_AGAIN_LATER.val
          && stageList !== ORDER_STAGE.APPOINTMENT_1.val
          && stageList !== ORDER_STAGE.APPOINTMENT_2.val
          && stageList !== ORDER_STAGE.APPOINTMENT_3.val
          && stageList !== ORDER_STAGE.APPOINTMENT_4.val) {
        stages.push(stageList);
      }
      return stages;
    }, []);

    const handleUpdateOrderStage = async (inputStage, id) => {
      try {
        const newStage = inputStage;
        const newStatus = Object.values(ORDER_STAGE).find((ob) => ob.val === inputStage).status;
        const payload = {
          stage: newStage,
          status: newStatus,
        };
        await OrderManagementService.updateStage(id, payload);
        if (!props.myOrder) {
          getOrderList();
        } else {
          getMyOrderList();
        }
        info('ORDER_UPDATE_SUCCESS');
        const data = {
          orderStage: inputStage,
          orderStatus: newStatus
        };
        logService.activityLog(logKeys.ORDER_UPDATED, data);
      } catch(e) {
        error(e);
      }
    };

    const claimOrder = async (id) => {
      try {
        const now = moment();

        const payload = {
          stage: ORDER_STAGE.ORDER_CLAIMED.val,
          status: ORDER_STATUS.IN_PROGRESS,
          step: ORDER_STEP.PENDING,
          dateClaim: now
        };
        const data = {
          orderId: id
        };

        await OrderManagementService.updateStage(id, payload);

        if (!props.myOrder) {
          getOrderList();
        } else {
          getMyOrderList();
        }
        info('ORDER_CLAIMED_SUCCESS');
        logService.activityLog(logKeys.ORDER_CLAIMED, data);
      } catch(e) {
        error(e);
      }
    };

    const startCall = (phoneNumber, orderId, orderStage, orderStatus, orderStep) => {
      setRef({ type: CALL_REFS.ORDER, refId: orderId });
      startOutboundCall(phoneNumber);

      const data = {
        orderId,
        orderStage,
        orderStatus,
        orderStep
      };

      logService.activityLog(logKeys.ORDER_CALL, data);

      OrderManagementService.setWorkingAgent(orderId);
    };

    const toggleNotes = (i) => {
      openingNote.value = !orderList.value[i].active ? orderList.value[i].id : null;

      const orders = orderList.value.map((order, index) => ({
        ...order,
        active: (i === index) ? !order.active : false
      }));
      orderList.value = orders;
    };

    const showOrderNoteModal = (orderId) => {
      idOrder.value = orderId;
      toggleOrderNoteModal();
    };

    const copyToClipboard = (val, event) => {
      event.stopPropagation();
      const el = document.createElement('textarea');
      el.value = val;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      info('COPY_PHONE_SUCCESS');
    };

    const getValidatorList = () => {
      if ((validators.value).length < 1) {
        getValidators();
      }
    };

    const showOrderDetail = (order) => {
      editOrder.value = true;
      orderDetail.value = order;
      if (order.marketingCampaign) {
        root.$router.push(applyParams(PATHS.ORDER_DETAIL, order.id));
      } else {
        toggleEditOrder();
      }

      getValidatorList();
    };

    const showCreateOrder = () => {
      editOrder.value = false;
      orderDetail.value = null;
      toggleEditOrder();
      getValidatorList();
    };

    const sorted = (name) => {
      const { sort } = filters.value;
      let index = sort.indexOf(name);
      if (index >= 0) {
        sort.splice(index, 1);
        sort.unshift(`-${name}`);
      } else {
        index = sort.indexOf(`-${name}`);
        sort.splice(index, 1);
        sort.unshift(name);
      }
      applyFilter({ sort });
    };

    const getIndexSort = (name) => {
      const index = filters.value.sort.indexOf(name);
      if (index >= 0) {
        return index + 1;
      }
      return filters.value.sort.indexOf(`-${name}`) + 1;
    };

    const canWorkOn = (item) => (!item.workingAgent || (item.workingAgent.username === profile.value.username));
    const { removeWorkingAgent } = OrderManagementService;

    const fetchOrderList = (showLoader = true) => (!props.myOrder ? getOrderList(showLoader) : getMyOrderList(showLoader));

    const stopEventShowOrderDetail = (event) => {
      event.stopPropagation();
    };

    watch(filters, () => {
      fetchOrderList();
    });

    watch(items, (val) => {
      orderList.value = val;
      if (openingNote.value) {
        const i = val.findIndex((o) => o.id === openingNote.value);
        if (i >= 0) {
          toggleNotes(i);
        }
      }
    });

    onMounted(() => {
      fetchOrderList();
      searchInput.value = filters.value.phone;
    });

    return {
      moment,
      faSearch,
      faPlus,
      faStickyNote,
      faPhone,
      faCopy,
      search,
      searchInput,
      changePage,
      changePageSize,
      total,
      orderList,
      filters,
      countUnclaimed,
      countPending,
      countContact,
      countQuoted,
      countAppointment,
      listStages,
      handleUpdateOrderStage,
      startCall,
      toggleNotes,
      copyToClipboard,
      claimOrder,
      getKeyStageTranslations,
      getKeyStepTranslations,
      ORDER_STEP,
      showOrderDetail,
      showCreateOrder,
      applyParams,
      PATHS,
      fetchOrderList,
      editOrder,
      getStages,
      orderDetail,
      canWorkOn,
      removeWorkingAgent,
      faSortUp,
      sorted,
      getIndexSort,
      isDesktop,
      stopEventShowOrderDetail,
      showOrderNoteModal,
      idOrder
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#order-management {
  padding: 30px;

  .header {
    display: flex;

    h1 {
      font-size: 28px;
      text-transform: capitalize;
    }
    #live-update-controller {
      padding-top: 3px;
      padding-left: 7px;
      font-size: 20px;
    }
  }

  ul {
    padding-inline-start: 0px;
    li {
      display: inline;
      margin-right: 15px;

      .highlight {
        color: $geranium;
      }
    }
  }
  .unclaimed {
    color: $geranium;
  }
  .pending {
    padding-left: 10px;
    color: $redViolet;
  }
  .contact {
    padding-left: 10px;
    color: $tuftsBlue;
  }
  .quoted {
    padding-left: 10px;
    color: $pizazz;
  }
  .appointment {
    padding-left: 10px;
    color: $aoEnglish;
  }
  .search {
    display: flex;
    justify-content: flex-end;
    margin-right: 20px;

    .input {
      width: 280px;
    }

    .primary-button {
      left: 20px;
      padding-left: 15px;
      padding-right: 15px;
      .icon {
        font-size: 25px;
        border: 2px solid white;
        border-radius: 50%;
        top: 3px;
        margin-right: 10px;
      }
    }
  }

  .list {
    margin-top: 20px;
    margin-bottom: 20px;

    .arrow{
      cursor: pointer;
      width: 32px;
      height: 28px;
      transform: rotate(0deg);
      transform-origin: center;
      transition: all .3s ease-in-out;
      // border: 1px solid $alto;

      svg {
        text-align: center;
        font-size: 28px;
        margin-top: 5px;
        height: 100%;
        width: 100%;
      }

      &.down {
        transform: rotate(180deg);
        transform-origin: center;

        svg {
          margin-top: 8px;
        }
      }
    }

    .number-circle {

      span {
        color: $babyPowder;
        background: $lavenderGrey;
        border-radius: 50%;
        -moz-border-radius: 50%;
        -webkit-border-radius: 50%;
        display: inline-block;
        line-height: 15px;
        text-align: center;
        width: 16px;
        font-size: 13px;
      }
    }

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

        tr {
          background-color: $babyPowder;

          th {
            padding: 15px 10px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;

            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }
          }
        }
      }

      tbody {
        text-align: center;

        tr {
          background-color: $babyPowder;
          transition: all .2s ease-in-out;

          &:hover {
            background-color: $ghostWhite;
            &.order-notes {
              background-color: $babyPowder;
            }
          }

          &.cant-work-on {
            cursor: not-allowed;
          }

          td {
            padding: 15px 5px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;

            &.phone {
              width: 110px;
            }

            .copy-icon {
              transition: all .2s ease-in-out;
              &:hover {
                color: $spanishGrey;
              }
            }

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
              color: $lavenderGrey;

              &.has-working-agent {
                color: $geranium;
              }
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }

            a {
              color: inherit;
            }

            .working-agent {
              line-height: 18px;

              &.can-remove {
                cursor: pointer;

                &:hover {
                  text-decoration: line-through;
                }
              }
            }

            &.actions {
              min-width: 150px;

              .icon {
                display: inline-block;
                width: 32px;
                height: 32px;
                text-align: center;
                position: relative;
                cursor: pointer;
                transition: all .2s ease-in-out;

                &::before {
                  content: "";
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background-color: $alto;
                  border-radius: 50%;
                  opacity: .2;
                  transition: all .2s ease-in-out;
                }

                svg {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translateX(-50%) translateY(-50%);
                }

                &:hover,

                &.call-btn-wrapper {
                  margin: 0 5px;
                  transition: all .2s ease-in-out;

                  &::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: $tuftsBlue;
                    border-radius: 50%;
                    opacity: .2;
                    transition: all .2s ease-in-out;
                  }

                  svg {
                    color: $tuftsBlue;
                    position: absolute;
                    width: 17px;
                    height: 17px;
                    top: 50%;
                    left: 50%;
                    transform: translateX(-50%) translateY(-50%);
                    transition: all .2s ease-in-out;
                  }

                  &:hover {
                    &::before {
                      opacity: 1;
                    }

                    svg {
                      color: $babyPowder;
                    }
                  }
                }

                &.toggle-notes {
                  margin: 0 5px;
                  transition: all .2s ease-in-out;

                  &::before {
                    background-color: $babyPowder;
                    opacity: 1;
                    border: 1px solid $alto;
                  }

                  svg {
                    top: calc(50% + 1px);
                  }

                  &.active {
                    transform: rotate(180deg);
                  }

                  &:hover {

                    svg {
                      color: $blueNcs;
                    }
                  }
                }
              }

              ul {
                li {
                  display: block;
                }
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 991px) {
    padding: 15px;

    .search {
      display: block;
      margin-bottom: 20px;
      margin-right: 0px;

      .input {
        width: 100%;
        margin-bottom: 16px;
        input {
          border-radius: 3px;
        }
      }

      .primary-button {
        width: 100%;
        left: 0px;
        padding-left: 15px;
        padding-right: 15px;
        border-radius: 3px;
      }
    }

    .list {
      margin-top: 10px;
      margin-bottom: 10px;
      display: block;

      table {
        width: auto;
        display: block;

        thead {
          display: block;
          tr {
            position: absolute;
            top: -9999px;
            left: -9999px;
          }
        }

        tbody {
          text-align: start;
          display: block;

          tr {
            display: block;
            border-top: 1px solid $alto;
            border-bottom: 1px sold $alto;

            &:nth-child() {
              background-color: grey;
            }

            td {
              padding: 5px;
              display:block;
              border: none;
              position: relative;
              padding-left: 45%;
              min-height: 30px;


              &::before {
                position: absolute;
                top: 0;
                left: 6px;
                width: 45%;
                padding-right: 10px;
                white-space: nowrap;
                text-align: center;
              }

              &.phone {
                width: 100%;
              }

              .mobile-header {
                position: absolute;
                // top: 0;
                left: 6px;
                // width: 45%;
                padding-right: 10px;
                white-space: nowrap;
                text-align: center;
                padding-left: 10px;
              }

              &:first-child {
                border: 0;
                padding-top: 20px;
              }

              &:last-child {
                border: 0;
                padding-bottom: 20px;
              }

              &.toggle-notes {
                margin: 0 5px;
                transition: all .2s ease-in-out;

                &::before {
                  background-color: $babyPowder;
                  opacity: 1;
                  border: 1px solid $alto;
                }

                svg {
                  top: calc(50% + 1px);
                }

                &.active {
                  transform: rotate(180deg);
                }

                &:hover {

                  svg {
                    color: $blueNcs;
                  }
                }
              }

              &.actions {

                .toggle-box {

                  .toggle-box-wrapper {
                    right: calc(-500%);
                    padding: unset;

                    ul {
                      li {
                        display: block;
                      }
                    }

                    &::before {
                      right: 53%;
                    }
                  }
                }
              }
            }

            &.order-notes {

              td {
                padding-left: unset;
                min-height: unset;

                &:first-child {
                  padding-top: unset;
                }

                &:last-child {
                  padding-bottom: 7px;
                }
              }

              .notes-wrapper {
                height: unset;
              }

              &.collapsed {

                .notes-wrapper {
                  height: 0;
                }
              }
            }
          }
        }
      }

    }
  }
}
</style>
